import PrivateVars from '../PrivateVars';
import ArrayUtil from '../ArrayUtil';
import Shader from './Shader.js';

// 
const VERTEX_LAYOUT = [
    { name: 'a_position', size: 3 },
    { name: 'a_normal', size: 3 },
    { name: 'a_texco', size: 2 }
]

export default class Mesh {

    static get standardVertexLayout() { return VERTEX_LAYOUT; }

    /**
     * Creates a new mesh instance.
     * @param {!Renderer} renderer An instance of renderer.
     * @param {!Object} data Mesh geometry / data.
     */
    constructor(renderer, data) {
        if (!renderer) throw new Error("Unable to create Mesh instance, invalid renderer argument given.");
        if (!data) throw new Error("Unable to create Mesh instance, invalid data argument given.");

        // Store reference to renderer
        this.renderer = renderer;

        this.data = {
            vertices: data.vertices,
            indices: data.indices
        };

        let gl = renderer.gl;

        // Vertex layout
        let layout = this.data.layout = {};
        layout.attributes = data.attributes || Mesh.standardVertexLayout;
        layout.stride = 4 * layout.attributes.reduce((x, y) => x + y.size, 0);

        // Create buffer data
        let vertices_buffer = new Float32Array(ArrayUtil.flatten(data.vertices));
        let indices_buffer = new Uint16Array(data.indices);

        // console.log(`Creating Mesh w/ ${vertices_buffer.length} vertex values and ${indices_buffer.length / 3} index values.`)
        // console.log(`Creating Mesh w/ ${data.vertices.length} vertices and ${data.indices.length / 3} triangles.`)

        // Update Vertex Buffer
        let vertexBuffer = gl.createBuffer()
        gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertices_buffer, gl.STATIC_DRAW);
        gl.bindBuffer(gl.ARRAY_BUFFER, null);

        // Update Index Buffer
        let indexBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices_buffer, gl.STATIC_DRAW);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);

        // Store buffers
        this.data.buffers = [vertexBuffer, indexBuffer];
    }

    /**
     * Draws this mesh with the given shader.
     * @param {!Shader} shader Shader to draw mesh with.
     * @param {?Object} uniforms Optional uniforms to set before drawing.
     */
    draw(shader, uniforms) {

        if (!shader)
            throw new Error("Unable to draw mesh, invalid shader argument.");

        // Enable shader
        PrivateVars.get(shader, 'use')();

        // Update uniforms
        if (uniforms) {
            for (let key in uniforms) {
                shader.uniform(key, uniforms[key]);
            }
        }

        // Draw mesh
        let data = this.data;
        let gl = this.renderer.gl;

        let shaderProgram = Shader.ActiveShader;

        // Bind Vertex Buffer
        gl.bindBuffer(gl.ARRAY_BUFFER, data.buffers[0]);

        // Bind Index Buffer
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, data.buffers[1]);

        // Configure Attributes
        let offset = 0;
        this.data.layout.attributes.forEach((attr, index) => {
            // TODO: Get index from shaderProgram
            gl.vertexAttribPointer(index, attr.size, attr.type || gl.FLOAT, false, data.layout.stride, offset);
            gl.enableVertexAttribArray(index);
            offset += attr.size * 4; // TODO: From type size
        });

        // Draw Mesh ( via indices )
        // console.log(`Drawing ${data.indices.length / 3} triangles.`);
        gl.drawElements(gl.TRIANGLES, data.indices.length, gl.UNSIGNED_SHORT, 0);
    }

    /**
     * Free's up the WebGL resources used by this instance.  
     * This makes the instance unusable afterwards.
     */
    dispose() {
        // TODO: Use a WeakMap in the Renderer, when the reference is 
        // collected, free the buffers then?
        gl.deleteBuffer(this.data.buffers[0]);
        gl.deleteBuffer(this.data.buffers[1]);
        this.data = undefined;
    }
}